import React, { useCallback, useEffect, useState } from 'react';
import { TableCell } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { Lock, Plus, Upload } from 'lucide-react';
import axios from 'axios';
import CustomTooltip from '../../../components/CustomTooltip/CustomTooltip';
import AnalyticsService from '../../../services/AnalyticsService/AnalyticsService';
import SearchBar from '../../../components/SearchBar/SearchBar';
import IntentSummary from '../../../types/IntentSummary';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useAuthRoles from '../../../hooks/useAuthRoles';
import useAbortController from '../../../hooks/useAbortController';
import Button from '../../../components/Button/Button';
import { HeaderDefinition } from '../../../components/DataTable/DataTableHeader';
import HighlightText from '../../../components/HighlightText/HighlightText';
import TopicsContainer from '../../../components/TopicsContainer/TopicsContainer';
import { parseMarkdown } from '../../../utils/markdownUtils';
import renderMarkdownParts from '../../../utils/renderMarkdownParts';
import DataTable from '../../../components/DataTable/DataTable';
import ImportFaqModal from './ImportFaqModal/ImportFaqModal';

interface QuestionsTabProps {
  returnQuery?: string;
  returnPage?: number;
  returnResultsPerPage?: number;
}

function QuestionsTab({
  returnQuery,
  returnPage,
  returnResultsPerPage
}: Readonly<QuestionsTabProps>): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState<string>(
    returnQuery ?? searchParams.get('search') ?? ''
  );
  const [page, setPage] = useState<number>(
    returnPage ?? +(searchParams.get('page') ?? 0)
  );
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [searchData, setSearchData] = useState<IntentSummary[]>();
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(true);
  const [resultsPerPage, setResultsPerPage] = useState<number>(
    returnResultsPerPage ?? +(searchParams.get('resultsPerPage') ?? 25)
  );
  const [hasError, setHasError] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const location = useLocation();
  const { userCanWrite } = useAuthRoles();

  function updateQueryParams(): void {
    setSearchParams((params) => {
      params.set('search', query ?? '');
      params.set('page', page.toString());
      params.set('resultsPerPage', resultsPerPage.toString());
      return params;
    });
  }
  const navigate = useNavigate();

  const { currentChatbot } = React.useContext(ChatbotContext);

  const handleClick = (): void => {
    if (!currentChatbot) return;

    const navigationState = {
      returnQuery: query,
      returnPage: page,
      returnResultsPerPage: resultsPerPage
    };

    navigate(`/bot-builder/${currentChatbot.id}/new`, {
      state: navigationState
    });
  };

  const { getAbortSignal } = useAbortController();

  const handleFetchIntents = useCallback(async (): Promise<void> => {
    if (!currentChatbot) return;

    const signal = getAbortSignal();

    setSearchIsLoading(true);

    AnalyticsService.getIntents({
      chatbotId: currentChatbot.id,
      query,
      page,
      resultsPerPage,
      signal
    })
      .then((data) => {
        updateQueryParams();
        setNumberOfResults(data.numberOfResults);
        setSearchData(data.results);
        setHasError(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setNumberOfResults(0);
        setSearchData([]);
        setHasError(true);
      })
      .finally(() => setSearchIsLoading(false));
  }, [currentChatbot, query, page, resultsPerPage, getAbortSignal]);

  useEffect(() => {
    handleFetchIntents();
  }, [handleFetchIntents, refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  useEffect(() => {
    if (returnQuery !== undefined) {
      setQuery(returnQuery);
    }
    if (returnPage !== undefined) {
      setPage(returnPage);
    }
    if (returnResultsPerPage !== undefined) {
      setResultsPerPage(returnResultsPerPage);
    }
  }, [returnQuery, returnPage, returnResultsPerPage]);

  const handleRowClick = (item: IntentSummary): void => {
    if (!currentChatbot) return;

    const navigationState = {
      returnQuery: query,
      returnPage: page,
      returnResultsPerPage: resultsPerPage
    };

    navigate(`/bot-builder/${currentChatbot.id}/${item.id}`, {
      state: navigationState
    });
  };

  if (!userCanWrite()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  const questionsHeaders: HeaderDefinition[] = [
    {
      key: 'name',
      label: 'Question name',
      className: 'w-[24%]'
    },
    {
      key: 'topics',
      label: 'Topics',
      className: 'w-[14%]'
    },
    {
      key: 'responseContent',
      label: 'Response content',
      className: 'w-[62%]'
    }
  ];

  const renderRow = (question: IntentSummary): React.ReactElement => {
    const response: string = question.responseContent || '';

    const processedContent = renderMarkdownParts(
      parseMarkdown(response),
      query
    );
    return (
      <>
        <TableCell className='w-[24%]'>
          <div className='flex items-center gap-2'>
            <span className='block truncate pr-2' title={question.intentName}>
              <HighlightText
                text={question.intentName}
                searchQuery={query}
                size='sm'
              />
            </span>
            {question.locked && (
              <CustomTooltip content='This question is locked by a Japeto admin'>
                <Lock size={14} className='text-gray-500 flex-shrink-0' />
              </CustomTooltip>
            )}
          </div>
        </TableCell>
        <TableCell className='w-[14%]'>
          <TopicsContainer tags={question.categories} />
        </TableCell>
        <TableCell className='w-[62%]'>
          <div className='max-w-full overflow-hidden'>
            <div className='text-sm line-clamp-2 pr-2' title={response}>
              {processedContent}
            </div>
          </div>
        </TableCell>
      </>
    );
  };

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - Questions</title>
      </Helmet>
      <div className='mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4'>
        <SearchBar
          setQuery={setQuery}
          searchTermFromState={query}
          placeholder='Search questions...'
        />
        <div className='flex flex-wrap items-center gap-2 md:flex-shrink-0'>
          <Button
            onClick={() => setImportModalOpen(true)}
            variant='outline'
            icon={<Upload size='16' />}
          >
            Import CSV
          </Button>
          <Button onClick={handleClick} icon={<Plus size='16' />}>
            Add FAQ
          </Button>
        </div>
      </div>
      {currentChatbot && (
        <ImportFaqModal
          isOpen={importModalOpen}
          chatbotId={currentChatbot.id}
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}
      <DataTable<IntentSummary>
        searchData={searchData || null}
        numberOfResults={numberOfResults}
        resultsPerPage={resultsPerPage}
        isLoading={searchIsLoading}
        setResultsPerPage={setResultsPerPage}
        setPage={setPage}
        page={page}
        tableHeaders={questionsHeaders}
        renderRow={renderRow}
        setSortColumn={() => {}}
        setSortDirection={() => {}}
        handleRowClick={handleRowClick}
        hasError={hasError}
        handleFetch={handleFetchIntents}
        noDataTitle='No questions added'
        noDataMessage='Add a new FAQ to start training your chatbot'
        hasActiveSearch={!!query}
        noSearchResultsTitle='No questions found'
        noSearchResultsMessage="There aren't any questions matching your search."
      />
    </div>
  );
}

export default QuestionsTab;
